import { PureComponent } from "react";
import { connect } from "react-redux";
import { type ActionButtonProps } from "widgets";

import { type ISelectOption } from "@/app-utils/form.ts";
import { type Role } from "@/graphql.ts";
import { loadRoles } from "@/redux/modules/roles-admin.ts";
import { AUTHENTICATION_MODE, fetchNumberOfFreeSeats } from "@/redux/modules/users-admin.ts";
import { type IAppState } from "@/redux/reducer.ts";

import { Dialog } from "../core/overlays/Dialogs.js";
import { default as AddLdapUsersForm } from "./AddLdapUsersForm.js";
import { default as AddNewUserToDatabaseForm } from "./AddNewUserToDatabaseForm.js";

interface IAddNewUserDialogOwnProps {
  onClose: () => void;
  refreshListOfUsers: () => void;
}

interface IAddNewUserDialogAppStateProps {
  roles: Role[];
  numberOfFreeSeats: number;
  canCreateOracleUser: boolean;
  authenticationMode: string;
}

interface IAddNewUserDialogDispatchProps {
  loadRoles: () => Promise<Role>;
  loadNumberOfFreeSeats: () => Promise<number>;
}

export interface IAddNewUserDialogProps
  extends IAddNewUserDialogAppStateProps,
    IAddNewUserDialogDispatchProps,
    IAddNewUserDialogOwnProps {}

function mapStateToProps(state: IAppState): IAddNewUserDialogAppStateProps {
  return {
    roles: state.rolesAdmin.roles,
    numberOfFreeSeats: state.usersAdmin.numberOfFreeSeats,
    canCreateOracleUser: state.config.configuration && state.config.configuration.canCreateOracleUser,
    authenticationMode: state.config.configuration && state.config.configuration.authenticationMode,
  };
}

function mapDispatchToProps(dispatch): IAddNewUserDialogDispatchProps {
  return {
    loadRoles: () => dispatch(loadRoles()),
    loadNumberOfFreeSeats: () => dispatch(fetchNumberOfFreeSeats()),
  };
}

interface IAddNewUserDialogState {
  nextButtonProps?: ActionButtonProps;
  otherButtonProps?: ActionButtonProps;
  error?: string;
}

class AddNewUserDialogImpl extends PureComponent<IAddNewUserDialogProps, IAddNewUserDialogState> {
  public static statusOptions: ISelectOption[] = [
    { value: 1, label: "Active" },
    { value: 0, label: "Disabled" },
  ];

  constructor(props: IAddNewUserDialogProps) {
    super(props);

    this.state = {
      nextButtonProps: {
        label: "Save",
        isDisabled: true,
        onPress: () => {},
      },
      error: null,
    };
  }

  componentDidMount() {
    this.props.loadRoles();
  }

  updateDialogState = (dialogState: IAddNewUserDialogState) => {
    this.setState(dialogState);
  };

  closeDialog = () => {
    this.props.onClose();
  };

  isLdapEnabled = () => {
    return this.props.authenticationMode === AUTHENTICATION_MODE.LDAP;
  };

  render() {
    return (
      <Dialog
        mainActionButtonProps={this.state.nextButtonProps}
        headerProps={{ title: "Create User Account" }}
        closeFn={this.closeDialog}
        secondaryActionButtonProps={this.state.otherButtonProps}
        footerMessage={{ message: this.state.error }}
      >
        {!this.isLdapEnabled() ? (
          <AddNewUserToDatabaseForm
            roles={this.props.roles}
            updateDialogState={this.updateDialogState}
            numberOfFreeSeats={this.props.numberOfFreeSeats}
            onClose={this.closeDialog}
            authenticationMode={this.props.authenticationMode}
            canCreateOracleUser={this.props.canCreateOracleUser}
            refreshListOfUsers={this.props.refreshListOfUsers}
          />
        ) : (
          <AddLdapUsersForm
            roles={this.props.roles}
            updateDialogState={this.updateDialogState}
            numberOfFreeSeats={this.props.numberOfFreeSeats}
            onClose={this.closeDialog}
            refreshListOfUsers={this.props.refreshListOfUsers}
          />
        )}
      </Dialog>
    );
  }
}

const AddNewUserDialog = connect<
  IAddNewUserDialogAppStateProps,
  IAddNewUserDialogDispatchProps,
  IAddNewUserDialogOwnProps
>(
  mapStateToProps,
  mapDispatchToProps,
)(AddNewUserDialogImpl);

export { AddNewUserDialogImpl };
export default AddNewUserDialog;
